import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiArrowRight, FiHeart, FiSliders, FiX } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData, {
  productCategories,
  siteConfig,
} from "../Datas/productData";
import "../css/shop.scss";

export function ProductCard({ product, compact = false }) {
  const { wishlistItems, toggleWishlist } = useContext(CartContext);
  const [imageFailed, setImageFailed] = useState(false);
  const isWishlisted = wishlistItems.includes(product.id);

  return (
    <article className={`product-card${compact ? " product-card--compact" : ""}`}>
      <div className="product-card__media">
        {product.badge && <span className="product-card__badge">{product.badge}</span>}
        <button
          className={`product-card__wishlist${isWishlisted ? " is-active" : ""}`}
          type="button"
          onClick={() => toggleWishlist(product.id)}
          aria-label={`${isWishlisted ? "Remove" : "Add"} ${product.name} ${isWishlisted ? "from" : "to"} wishlist`}
        >
          <FiHeart aria-hidden="true" />
        </button>
        <Link to={`/shopDetails/${product.id}`} aria-label={`View ${product.name}`}>
          {imageFailed ? (
            <div className="product-card__image-fallback" role="img" aria-label={product.name}>
              <span>{product.category}</span>
            </div>
          ) : (
            <img
              src={product.image}
              alt={product.name}
              width="1086"
              height="1448"
              loading="lazy"
              decoding="async"
              onError={() => setImageFailed(true)}
            />
          )}
        </Link>
      </div>
      <div className="product-card__body">
        <span className="product-card__category">{product.category}</span>
        <h3><Link to={`/shopDetails/${product.id}`}>{product.name}</Link></h3>
        <div className="product-card__price">
          <span>{siteConfig.currencySymbol}{product.price.toFixed(2)}</span>
          {product.compareAtPrice && (
            <del>{siteConfig.currencySymbol}{product.compareAtPrice.toFixed(2)}</del>
          )}
        </div>
        <Link className="product-card__action" to={`/shopDetails/${product.id}`}>
          Choose options <FiArrowRight aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

function Shop() {
  const { wishlistItems } = useContext(CartContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "");
  const [sortBy, setSortBy] = useState("featured");
  const [priceRange, setPriceRange] = useState("all");
  const [selectedSize, setSelectedSize] = useState("all");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);

  const category = searchParams.get("category") || "All";
  const query = searchParams.get("search") || "";
  const showNew = searchParams.get("new") === "true";
  const showSale = searchParams.get("sale") === "true";
  const showWishlist = searchParams.get("wishlist") === "true";
  const fit = searchParams.get("fit") || "";

  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  useEffect(() => {
    setIsLoading(true);
    const timer = window.setTimeout(() => setIsLoading(false), 220);
    return () => window.clearTimeout(timer);
  }, [category, fit, query, showNew, showSale, showWishlist, sortBy, priceRange, selectedSize]);

  useEffect(() => {
    setPage(1);
  }, [category, fit, query, showNew, showSale, showWishlist, sortBy, priceRange, selectedSize]);

  const setParam = (name, value) => {
    const nextParams = new URLSearchParams(searchParams);
    if (!value || value === "All" || value === "false") {
      nextParams.delete(name);
    } else {
      nextParams.set(name, value);
    }
    setSearchParams(nextParams);
  };

  const filteredProducts = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return productData
      .filter((product) => category === "All" || product.category === category)
      .filter((product) => !fit || product.attributes.Fit.toLowerCase().includes(fit.toLowerCase()))
      .filter((product) => !showNew || product.newArrival)
      .filter((product) => !showSale || product.compareAtPrice)
      .filter((product) => !showWishlist || wishlistItems.includes(product.id))
      .filter((product) => {
        if (!normalizedQuery) return true;
        return [product.name, product.category, product.material, ...product.tags]
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);
      })
      .filter((product) => {
        if (priceRange === "under150") return product.price < 150;
        if (priceRange === "150to250") return product.price >= 150 && product.price <= 250;
        if (priceRange === "over250") return product.price > 250;
        return true;
      })
      .filter((product) => selectedSize === "all" || product.sizes.includes(selectedSize))
      .sort((first, second) => {
        if (sortBy === "newest") return Number(second.newArrival) - Number(first.newArrival) || second.id - first.id;
        if (sortBy === "best") return second.rating - first.rating || second.reviews - first.reviews;
        if (sortBy === "priceLow") return first.price - second.price;
        if (sortBy === "priceHigh") return second.price - first.price;
        if (sortBy === "alphabetical") return first.name.localeCompare(second.name);
        return Number(second.featured) - Number(first.featured) || first.id - second.id;
      });
  }, [category, fit, query, showNew, showSale, showWishlist, wishlistItems, priceRange, selectedSize, sortBy]);

  const itemsPerPage = 12;
  const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const pageProducts = filteredProducts.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const hasDataError = !Array.isArray(productData);

  const submitSearch = (event) => {
    event.preventDefault();
    setParam("search", searchInput.trim());
  };

  const clearFilters = () => {
    setSearchParams({});
    setPriceRange("all");
    setSelectedSize("all");
    setSortBy("featured");
    setSearchInput("");
  };

  const setCategory = (nextCategory) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("fit");
    if (nextCategory === "All") nextParams.delete("category");
    else nextParams.set("category", nextCategory);
    setSearchParams(nextParams);
  };

  return (
    <main className="shop">
      <header className="shop__hero">
        <span className="eyebrow">Durable goods, considered fits</span>
        <h1>{showWishlist ? "Your Wishlist" : showSale ? "Sale" : showNew ? "New Arrivals" : "Shop Our Collection"}</h1>
        <p>
          {showWishlist
            ? "The pieces you saved, gathered in one place."
            : "Premium denim, jackets and everyday workwear made to gain character with use."}
        </p>
      </header>

      <div className="shop__toolbar page-width">
        <button className="shop__filter-toggle" type="button" onClick={() => setFiltersOpen(true)}>
          <FiSliders aria-hidden="true" /> Filters
        </button>
        <form className="shop__search" onSubmit={submitSearch} role="search">
          <label className="sr-only" htmlFor="shop-search">Search the collection</label>
          <input
            id="shop-search"
            type="search"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search the collection"
          />
          <button type="submit">Search</button>
        </form>
        <div className="shop__result-count">{filteredProducts.length} products</div>
        <label className="shop__sort">
          <span>Sort by</span>
          <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
            <option value="featured">Featured items</option>
            <option value="newest">Newest items</option>
            <option value="best">Best selling</option>
            <option value="alphabetical">A to Z</option>
            <option value="priceLow">Price: ascending</option>
            <option value="priceHigh">Price: descending</option>
          </select>
        </label>
      </div>

      <div className="shop__layout page-width">
        <div className={`shop__filter-overlay${filtersOpen ? " is-open" : ""}`} onClick={() => setFiltersOpen(false)} />
        <aside className={`shop__filters${filtersOpen ? " is-open" : ""}`} aria-label="Product filters">
          <div className="shop__filters-head">
            <h2>Filter</h2>
            <button type="button" onClick={() => setFiltersOpen(false)} aria-label="Close filters"><FiX /></button>
          </div>

          <fieldset>
            <legend>Category</legend>
            {productCategories.map((item) => (
              <label key={item}>
                <input
                  type="radio"
                  name="category"
                  checked={category === item}
                  onChange={() => setCategory(item)}
                />
                <span>{item}</span>
              </label>
            ))}
          </fieldset>

          <fieldset>
            <legend>Price</legend>
            {[
              ["all", "All prices"],
              ["under150", "Under $150"],
              ["150to250", "$150 – $250"],
              ["over250", "Over $250"],
            ].map(([value, label]) => (
              <label key={value}>
                <input type="radio" name="price" checked={priceRange === value} onChange={() => setPriceRange(value)} />
                <span>{label}</span>
              </label>
            ))}
          </fieldset>

          <label className="shop__size-filter">
            <span>Available size</span>
            <select value={selectedSize} onChange={(event) => setSelectedSize(event.target.value)}>
              <option value="all">All sizes</option>
              {["30", "32", "34", "36", "S", "M", "L", "XL", "One Size"].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </label>

          <button className="shop__clear" type="button" onClick={clearFilters}>Clear all</button>
        </aside>

        <section className="shop__results" aria-live="polite">
          {hasDataError ? (
            <div className="state-panel state-panel--error">
              <h2>We couldn’t load the collection.</h2>
              <p>Please refresh the page and try again.</p>
            </div>
          ) : isLoading ? (
            <div className="shop__loading" aria-label="Loading products">
              {Array.from({ length: 6 }).map((_, index) => <span key={index} />)}
            </div>
          ) : pageProducts.length === 0 ? (
            <div className="state-panel">
              <h2>{showWishlist ? "Your wishlist is empty." : "No products found."}</h2>
              <p>{showWishlist ? "Save pieces from the shop and they’ll appear here." : "Try a broader search or clear the current filters."}</p>
              <button type="button" onClick={clearFilters}>View all products</button>
            </div>
          ) : (
            <>
              <div className="shop__grid">
                {pageProducts.map((product) => <ProductCard key={product.id} product={product} />)}
              </div>
              {totalPages > 1 && (
                <nav className="shop__pagination" aria-label="Product pages">
                  {Array.from({ length: totalPages }).map((_, index) => {
                    const pageNumber = index + 1;
                    return (
                      <button
                        key={pageNumber}
                        type="button"
                        className={page === pageNumber ? "is-active" : ""}
                        onClick={() => {
                          setPage(pageNumber);
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        aria-current={page === pageNumber ? "page" : undefined}
                      >
                        {String(pageNumber).padStart(2, "0")}
                      </button>
                    );
                  })}
                </nav>
              )}
            </>
          )}
        </section>
      </div>
    </main>
  );
}

export default Shop;
